var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { action, flow } from 'mobx';
import { Observer } from 'mobx-react-lite';
import React from 'react';
import { ContextColor } from '../../constants/color.enum';
import joinClassName from '../../utils/className.utils';
import { getContextColorStyle } from '../../utils/color.utils';
import { renderRenderable } from '../../utils/components.utils';
import { reportError } from '../../utils/errors.utils';
import { NoOp } from '../../utils/functions.utils';
import { useProps, useStore } from '../../utils/mobx.utils';
import BaseIcon from '../BaseIcon/BaseIcon';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import './BaseButton.scss';
var BaseButton = function (props) {
    var p = useProps(props);
    var s = useStore(function () { return ({
        get isLoading() {
            return p.loading || s.awaitingAction;
        },
        get noop() {
            return !!((p.onClick && p.onClick === NoOp) || (!p.onClick && !p.href));
        },
        hovered: false,
        awaitingAction: false,
        handleButtonClick: flow(function (e) {
            var onClickPromise, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, 4, 5]);
                        if (p.disabled)
                            return [2 /*return*/];
                        if (p.loading)
                            return [2 /*return*/];
                        e.stopPropagation();
                        onClickPromise = p.onClick && p.onClick();
                        if (!(onClickPromise instanceof Promise)) return [3 /*break*/, 2];
                        s.awaitingAction = true;
                        return [4 /*yield*/, onClickPromise];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [3 /*break*/, 5];
                    case 3:
                        e_1 = _a.sent();
                        reportError(e_1);
                        return [3 /*break*/, 5];
                    case 4:
                        s.awaitingAction = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }),
        handleHover: action(function () { return s.hovered = true; }),
        handleUnhover: action(function () { return s.hovered = false; }),
        get className() {
            var name = p.name, propsClassName = p.className, _a = p.appearance, appearance = _a === void 0 ? 'default' : _a, fullWidth = p.fullWidth, disabled = p.disabled, _b = p.padded, padded = _b === void 0 ? false : _b, _c = p.size, size = _c === void 0 ? 'md' : _c, rounded = p.rounded;
            return joinClassName('BaseButton', "BaseButton--" + appearance, propsClassName, size, name, fullWidth && 'fullWidth', disabled && 'disabled', s.isLoading && 'isLoading', padded && 'padded', rounded && 'rounded', p.circle && 'circle', s.noop && 'noop', p.hoverLabel && 'hasHoverLabel');
        },
        get iconVariant() {
            var _a;
            return (_a = p.iconVariant) !== null && _a !== void 0 ? _a : (p.size === 'xs' || p.size === 'sm' ? 'filled' : 'regular');
        },
        get children() {
            var icon = p.icon, children = p.children, label = p.label, hoverLabel = p.hoverLabel, hoverIcon = p.hoverIcon;
            return React.createElement(React.Fragment, null,
                React.createElement("div", { className: "BaseButtonInner" },
                    React.createElement("div", { className: "BaseButtonStaticLabel" },
                        icon && React.createElement(BaseIcon, { name: icon, variant: s.iconVariant, size: p.iconSize }),
                        (children || label) && React.createElement("div", { className: "BaseButtonLabel" },
                            renderRenderable(label),
                            " ",
                            children)),
                    hoverLabel && React.createElement("div", { className: "BaseButtonHoverLabel" },
                        (hoverIcon || icon) && React.createElement(BaseIcon, { name: hoverIcon !== null && hoverIcon !== void 0 ? hoverIcon : icon, variant: p.iconVariant }),
                        React.createElement("div", { className: "BaseButtonLabel" }, hoverLabel))),
                s.isLoading && React.createElement(LoadingIndicator, null));
        },
        get style() {
            var _a = p.hoverColor, hoverColor = _a === void 0 ? p.color : _a, color = p.color;
            var colorStyle = getContextColorStyle(ContextColor.Primary, s.hovered ? hoverColor : color);
            return __assign(__assign({}, colorStyle), { minWidth: p.minWidth, backgroundImage: p.backgroundImage });
        },
        get commonAttr() {
            var name = p.name, title = p.title, disabled = p.disabled;
            return {
                'data-name': name,
                title: title || name,
                className: s.className,
                style: s.style,
                children: s.children,
                onClick: s.handleButtonClick,
                disabled: disabled,
                onMouseEnter: s.handleHover,
                onMouseLeave: s.handleUnhover,
                ref: p.innerRef,
                'data-cy': p.dataCy,
                rel: p.target === '_blank' ? 'noreferrer' : undefined,
            };
        }
    }); });
    return React.createElement(Observer, { children: function () {
            var href = p.href, type = p.type, target = p.target;
            switch (true) {
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                case (!!href): return React.createElement("a", __assign({ href: href }, s.commonAttr, { target: target }));
                default: return React.createElement("button", __assign({ type: type }, s.commonAttr));
            }
        } });
};
export default BaseButton;
